import { ApplicationView, useController } from '@aha-app/mvc';
import classNames from 'classnames';
import React, {
  type ComponentType,
  useCallback,
  type MouseEventHandler,
} from 'react';
import styles from '../styles.module.css';
import useResizeObserver from '../components/hooks/useResizeObserver';
import HoverController, {
  type HoveredLink,
} from '../controllers/HoverController';
import type Canvas from '../objects/Canvas';
import WhiteboardController from '../controllers/WhiteboardController';
import { sanitizeUrl } from '../utils/sanitizer';

interface HoveredLinkPopupProps {
  canvas: Canvas;
}

const HoveredLinkPopup = ({ canvas }: HoveredLinkPopupProps) => {
  const hoverController = useController(HoverController);
  const whiteboardController = useController(WhiteboardController);
  const hoveredLink = hoverController.hoveredLink;
  const hoveredLinkTooltipPosition =
    hoverController.state.hoveredLinkTooltipPosition;

  const navigate = useCallback(
    (url: string) => {
      hoverController.actionLinkClicked();

      const link = whiteboardController.parseInternalLink(url);

      // check if it's an internal link. If so, hijack it and navigate within the whiteboard
      if (link?.hash) {
        const newHash = '#' + link.hash;

        // in some cases we need to avoid changing the hash, so we navigate without changing it if
        if (
          // we are in a client-side routing context (my work), to avoid triggering the router
          whiteboardController.clientSideRouting() ||
          // the current location does not match the current whiteboard (i.e. we are in the drawer),
          // since that wouldn't make sense
          !whiteboardController.parseInternalLink(window.location.href) ||
          // it is the same as the existing url, since hashchange won't fire if it doesn't change
          window.location.hash === newHash
        ) {
          whiteboardController.actionNavigate(link?.hash);
        } else {
          // otherwise update the hash, and let the WhiteboardController hashchange listener handle the change
          window.location.hash = newHash;
        }

        return true;
      }

      // let the browser handle the link natively
      return false;
    },
    [whiteboardController]
  );

  if (!hoveredLink) {
    return null;
  }

  return (
    <HoveredLinkPopupContent
      canvas={canvas}
      hoveredLink={hoveredLink}
      onMouseOver={() => hoverController.actionPopupHovered(true)}
      onMouseOut={() => hoverController.actionPopupHovered(false)}
      navigate={navigate}
      position={hoveredLinkTooltipPosition}
    />
  );
};

interface HoveredLinkPopupContentProps {
  hoveredLink: HoveredLink;
  canvas: Canvas;
  onMouseOver: () => void;
  onMouseOut: () => void;
  navigate: (url: string) => boolean;
  position?: 'top';
}
const HoveredLinkPopupContent = ({
  hoveredLink,
  canvas,
  onMouseOver,
  onMouseOut,
  navigate,
  position,
}: HoveredLinkPopupContentProps) => {
  const coords = canvas.rectToWindowCoords(hoveredLink.rect);

  // Position hover popup relative to the link rect. Prefer below center, and
  // prevent it going off the screen
  const calculateDimensions = useCallback(() => {
    if (!toolbarRef.current || !canvas.vptCoords) {
      return;
    }

    const vpt = canvas.vptCoords;
    const viewport = canvas.rectToWindowCoords({
      left: vpt.tl.x,
      top: vpt.tl.y,
      width: vpt.tr.x - vpt.tl.x,
      height: vpt.bl.y - vpt.tl.y,
    });
    const LEFT_MARGIN = 85;
    const RIGHT_MARGIN = 20;

    // should match spill height in .toolbarSpill class
    const SPACER = 8;

    const dimensions = toolbarRef.current.getBoundingClientRect();

    const toolbarLeft = Math.max(
      LEFT_MARGIN,
      Math.min(
        coords.left + coords.width / 2 - dimensions.width / 2,
        viewport.width - dimensions.width - RIGHT_MARGIN
      )
    );

    let toolbarTop: number;

    const notEnoughSpaceBelow =
      coords.top + coords.height + SPACER + dimensions.height > viewport.height;
    // NOTE: This does not take the static aha-header into account. So if the
    // page is scrolled down, the header might obscure the popup.
    const enoughSpaceAbove =
      coords.top - SPACER - dimensions.height > viewport.top;

    if (notEnoughSpaceBelow || (position === 'top' && enoughSpaceAbove)) {
      // above
      toolbarTop = coords.top - dimensions.height - SPACER;
    } else {
      // below
      toolbarTop = coords.top + coords.height + SPACER;
    }

    toolbarRef.current.style.left = `${toolbarLeft}px`;
    toolbarRef.current.style.top = `${toolbarTop}px`;
  }, [canvas, coords.height, coords.left, coords.top, coords.width]);

  const toolbarRef = useResizeObserver<HTMLDivElement>(calculateDimensions);

  const onClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
    e => {
      if (navigate(hoveredLink.href)) {
        e.preventDefault();
      }
    },
    [hoveredLink.href, navigate]
  );

  return (
    <div
      className={classNames(
        styles.linkPopup,
        styles.toolbarCommon,
        styles.toolbarSpill
      )}
      onMouseOver={onMouseOver}
      onMouseOut={onMouseOut}
      ref={toolbarRef}
    >
      <a
        href={sanitizeUrl(hoveredLink.href)}
        target='_blank'
        rel='noreferrer noopener'
        onClick={onClick}
      >
        {hoveredLink.href.length > 50
          ? hoveredLink.href.slice(0, 50) + '…'
          : hoveredLink.href}
      </a>
    </div>
  );
};

export default ApplicationView<ComponentType<HoveredLinkPopupProps>>(
  HoveredLinkPopup
);
